import type { FunctionComponent } from 'preact';
import { useRef } from 'preact/hooks';

import Loading from 'Components/smallComponents/loading';
import Select, {
  type Entry,
  Params as SelectParams,
  SelectContextProvider,
} from 'Components/smallComponents/select';
import type { RepeaterFunctionInvoker, TemplateFunction } from 'Components/types';
import { discardField, discardManyFields, replaceValue, toggle } from 'Core/actions/request';
import { useCollapsible } from 'Core/hooks/collapsible';
import { createFilteredFacetsSelector } from 'Core/selectors/search';
import { resultsLoadingSelector } from 'Core/selectors/show';
import { useDispatch, useSelector } from 'Core/store';
import { DisplayMode } from 'Modules/serverApi/types';
import { cloneSafe } from 'Utils/components';
import isMobile from 'Vendor/isMobile';

export type Params = {
  selects: RepeaterFunctionInvoker<SelectParams>; // TODO: make params
  hasSelection: boolean;
  discardFields: () => void;
  isBarCollapsed: boolean;
  collapseBar: () => void;
};

interface Props {
  template: TemplateFunction<Params>;
  fields: string[];
  initCollapsed?: boolean;
  useNativeDropdown?: boolean;
  minFacetCount?: number;
  showHitCount?: boolean;
}

const FacetBar: FunctionComponent<Props> = ({
  template,
  fields,
  initCollapsed,
  useNativeDropdown,
  minFacetCount = 1,
  showHitCount = false,
}) => {
  const dispatch = useDispatch();

  const rootRef = useRef<HTMLElement>(null);
  const { isCollapsed: isBarCollapsed, toggle: collapseBar } = useCollapsible(
    rootRef,
    null,
    initCollapsed ?? isMobile,
  );

  const facets = useSelector(createFilteredFacetsSelector(fields));

  if ((facets?.length ?? 0) < minFacetCount) {
    return null;
  }

  const discardFields = () => dispatch(discardManyFields(fields));

  const loading = <Loading selector={resultsLoadingSelector} key="loading" />;

  const selects = facets.map((facet) => (templ: TemplateFunction<SelectParams>) => {
    const { displayMode } = facet;
    const isMultiSelect = displayMode === DisplayMode.MultiSelect || displayMode === DisplayMode.Checkbox;

    const props = {
      template: templ,
      entries: facet.facetedValues.map(
        (v): Entry => ({
          value: v.value,
          term: v.term,
          selected: v.isSelected,
          hitCount: v.hitCount,
        }),
      ),
      extraClassName: 'cm_facet_select',
      field: facet.field,
      title: facet.name,
      hideNullOption: true,
      showCheckboxes: isMultiSelect,
      useNativeDropdown: useNativeDropdown ?? !isMultiSelect,
      showHitCount,
      onChange: (term: string) =>
        dispatch(
          !term
            ? discardField(facet.field, { mayDiscardValue: true })
            : isMultiSelect
              ? toggle({ field: facet.field, term }, { isSingleValue: !isMultiSelect, mayDiscardValue: true })
              : replaceValue({ field: facet.field, term }, { mayDiscardValue: true }),
        ),
    };
    return <Select {...props} key={`facet-bar-select-${facet.field}`} />;
  }) as RepeaterFunctionInvoker<SelectParams>;

  const component = template.call({
    selects,
    hasSelection: facets.selection.length > 0,
    discardFields,
    isBarCollapsed,
    collapseBar,
  });

  return (
    <SelectContextProvider>
      {cloneSafe(component, rootRef, { appendedChildren: loading })}
    </SelectContextProvider>
  );
};

export default FacetBar;
